import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import type { CreativeLearningOperationsReport } from '../../creativeDecisions';

interface CreativeLearningOperationsSummaryProps {
  report: CreativeLearningOperationsReport | null;
  loading?: boolean;
  error?: string | null;
}

/**
 * Secondary operations evidence for the experimentation program. The
 * money-first deployment portfolio remains the primary operator queue; this
 * panel describes whether the system is actually producing reusable,
 * replicated learning across completed experiments.
 */
export function CreativeLearningOperationsSummary({
  report,
  loading = false,
  error,
}: CreativeLearningOperationsSummaryProps): React.ReactElement {
  if (loading && !report) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }} aria-label="Learning system health loading">
        <Stack spacing={1.25}>
          <Skeleton width={220} height={28} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(6, 1fr)' }, gap: 1 }}>
            {Array.from({ length: 6 }, (_value, index) => (
              <Skeleton key={index} variant="rounded" height={72} />
            ))}
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (error && !report) {
    return (
      <Alert severity="warning">
        Learning-system health is unavailable: {error}. The money-flow portfolio remains usable.
      </Alert>
    );
  }

  if (!report) return <></>;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
      <Stack spacing={1.5}>
        {error ? (
          <Alert severity="warning">
            Latest learning-system refresh failed: {error}. Showing the last verified report below.
          </Alert>
        ) : null}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <ScienceOutlinedIcon color="action" fontSize="small" />
              <Typography variant="h6" sx={{ fontWeight: 750 }}>Learning system health</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Experiment throughput, reusable findings, replication, and mature decision regret. This evidence does not create a global creative winner.
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {loading ? 'Refreshing · ' : ''}As of {formatDateTime(report.asOf)}
          </Typography>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(6, 1fr)' }, gap: 1 }}>
          <LearningMetric
            label="Experiment throughput"
            value={`${formatNumber(report.experimentThroughputPer30Days)} / 30d`}
            detail={`${report.completedExperiments.toLocaleString()} completed in window`}
          />
          <LearningMetric
            label="Promoted findings"
            value={report.promotedFindingCount.toLocaleString()}
            detail={`${report.decisionInformingFindingCount.toLocaleString()} informed a decision`}
          />
          <LearningMetric
            label="Replication coverage"
            value={formatRate(report.replicationCoverage)}
            detail={`${report.passedReplicationCount.toLocaleString()} passed of ${report.replicationAttemptCount.toLocaleString()} attempts`}
          />
          <LearningMetric
            label="Learning yield"
            value={report.learningYield === null ? 'Unavailable' : formatNumber(report.learningYield)}
            detail="Decision-informing findings / experiment"
          />
          <LearningMetric
            label="Decision regret"
            value={formatRate(report.decisionRegretRate)}
            detail={`${report.regrettedDecisionCount.toLocaleString()} of ${report.decisionsWithMatureCounterfactual.toLocaleString()} mature comparisons`}
          />
          <LearningMetric
            label="Regret contribution"
            value={formatRegretHeadline(report)}
            detail={formatRegretDetail(report)}
          />
        </Box>

        {report.unavailableReasonCodes.length > 0 && (
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">Unavailable evidence:</Typography>
            {report.unavailableReasonCodes.map((reason) => (
              <Chip key={reason} size="small" variant="outlined" label={humanize(reason)} />
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function LearningMetric({ label, value, detail }: {
  label: string;
  value: string;
  detail: string;
}): React.ReactElement {
  return (
    <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{detail}</Typography>
    </Box>
  );
}

function formatRate(value: number | null): string {
  if (value === null) return 'Unavailable';
  return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(value));
}

function formatRegretHeadline(report: CreativeLearningOperationsReport): string {
  if (report.totalRegretContributionMinor !== null
    && report.totalRegretCurrency
    && report.totalRegretCurrencyExponent !== null) {
    return formatMinor(
      report.totalRegretContributionMinor,
      report.totalRegretCurrency,
      report.totalRegretCurrencyExponent,
    );
  }
  if (report.regretContributionByCurrency.length > 1) {
    return `${report.regretContributionByCurrency.length} currencies`;
  }
  return 'Unavailable';
}

function formatRegretDetail(report: CreativeLearningOperationsReport): string {
  if (!report.regretContributionByCurrency.length) return 'Contribution basis · missing is not zero';
  return report.regretContributionByCurrency.map((bucket) => (
    formatMinor(bucket.amountMinor, bucket.currency, bucket.currencyExponent)
  )).join(' · ');
}

function formatMinor(amountMinor: number, currency: string, exponent: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  }).format(amountMinor / 10 ** exponent);
}

function humanize(value: string): string {
  return value.replaceAll('_', ' ');
}
